import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Edit,
  Trash2,
  Tag,
  FolderKanban,
  ListTodo,
  MoreVertical,
  Power,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import {
  getStatuses,
  deleteStatus,
  toggleStatusActive,
  updateStatus,
  StatusConfig,
} from '@/services/status.service';
import { AddStatusDialog } from '@/components/hiring/AddStatusDialog';
import { clearStatusCache } from '@/utils/statusUtils';

const ProjectSettings: React.FC = () => {
  const { user } = useAuthStore();

  // Check if user is Super Admin
  const isSuperAdmin = user?.role?.toLowerCase().includes('super') || false;

  // Permission checks - check for projectStatus permissions
  const projectPermissions = (user?.permissions as any)?.project_management;
  const projectStatusPermissions = projectPermissions?.projectStatus;

  // Permission checks for status management
  // Super Admin has all permissions
  const canCreateStatus = isSuperAdmin || projectStatusPermissions?.create === true;
  const canEditStatus = isSuperAdmin || projectStatusPermissions?.edit === true;
  const canDeleteStatus = isSuperAdmin || projectStatusPermissions?.delete === true;

  // Check if user has access to project settings
  // User needs projectStatus.view permission to access the page (or be Super Admin)

  // State for status management
  const [statuses, setStatuses] = useState<StatusConfig[]>([]);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<StatusConfig | null>(null);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true);
  const [activeTab, setActiveTab] = useState('statuses');

  // Fetch statuses function
  const fetchStatuses = async () => {
    try {
      setIsLoadingStatuses(true);

      const data = await getStatuses();

      // Filter only project and task statuses
      const projectAndTaskStatuses = data.filter(
        status => status.category === 'project' || status.category === 'task'
      );

      setStatuses(projectAndTaskStatuses);

      if (projectAndTaskStatuses.length === 0) {
        toast.warning('No statuses found. Please check if statuses are configured.');
      }

    } catch (err) {
      toast.error('Failed to fetch statuses. Please try again.');
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  // Fetch statuses from API on mount
  React.useEffect(() => {
    fetchStatuses();
  }, []);

  // Reset forms
  const resetStatusForm = () => {
    setEditingStatus(null);
  };

  // Status management functions
  const handleAddStatus = async (newStatus: StatusConfig) => {
    // Allow creation only if user has create permission
    if (!canCreateStatus) {
      toast.error('You do not have permission to create statuses');
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading('Creating status...');

      // Add the new status to local state immediately for optimistic update
      setStatuses(prev => [...prev, newStatus]);

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Status "${newStatus.name}" created successfully`);

      setIsStatusDialogOpen(false);
      resetStatusForm();

      // Refresh from backend to ensure consistency
      await fetchStatuses();

      // Clear status cache so other components refresh
      clearStatusCache();

    } catch (err: any) {
      // Remove the optimistically added status on error
      setStatuses(prev => prev.filter(status => status.id !== newStatus.id));

      let errorMessage = 'Failed to create status';

      if (err.response) {
        if (err.response.status === 409) {
          errorMessage = 'Status name already exists';
        } else if (err.response.status === 403) {
          errorMessage = 'You do not have permission to create statuses';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection';
      }

      toast.error(errorMessage);
    }
  };

  const handleEditStatus = (status: StatusConfig) => {
    // Allow editing only if user has edit permission
    if (!canEditStatus) {
      toast.error('You do not have permission to edit statuses');
      return;
    }

    if (status.isSystem) {
      toast.error('System statuses cannot be edited');
      return;
    }
    setEditingStatus(status);
    setIsStatusDialogOpen(true);
  };

  const handleUpdateStatus = async (updatedStatus: StatusConfig) => {
    // Allow updating only if user has edit permission
    if (!canEditStatus) {
      toast.error('You do not have permission to edit statuses');
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading('Updating status...');

      // Optimistically update the UI immediately
      setStatuses(prev => prev.map(status =>
        status.id === updatedStatus.id ? updatedStatus : status
      ));

      // Make the actual API call
      await updateStatus(updatedStatus.id, {
        name: updatedStatus.name,
        description: updatedStatus.description,
        color: updatedStatus.color,
        category: updatedStatus.category,
      });

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Status "${updatedStatus.name}" updated successfully`);

      setIsStatusDialogOpen(false);
      resetStatusForm();

      // Refresh from backend to ensure consistency
      await fetchStatuses();

      // Clear status cache so other components refresh
      clearStatusCache();

    } catch (err: any) {
      // Revert the optimistic update on error
      await fetchStatuses();

      let errorMessage = 'Failed to update status';

      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = 'Status not found';
        } else if (err.response.status === 403) {
          errorMessage = 'You do not have permission to update this status';
        } else if (err.response.status === 409) {
          errorMessage = 'Status name already exists';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection';
      }

      toast.error(errorMessage);
    }
  };

  const handleToggleStatusActive = async (status: StatusConfig) => {
    // Allow toggling only if user has edit permission
    if (!canEditStatus) {
      toast.error('You do not have permission to edit statuses');
      return;
    }
    try {
      const newActiveState = !status.isActive;

      // Optimistically update the UI immediately
      setStatuses(prevStatuses =>
        prevStatuses.map(s =>
          s.id === status.id
            ? { ...s, isActive: newActiveState }
            : s
        )
      );

      // Show loading toast
      const loadingToast = toast.loading(`${newActiveState ? 'Activating' : 'Deactivating'} status...`);

      // Make API call
      await toggleStatusActive(status.id, newActiveState);

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Status "${status.name}" ${newActiveState ? 'activated' : 'deactivated'} successfully`);

      // Clear status cache so other components refresh
      clearStatusCache();

    } catch (err: any) {
      // Revert the optimistic update on error
      setStatuses(prevStatuses =>
        prevStatuses.map(s =>
          s.id === status.id
            ? { ...s, isActive: !status.isActive }
            : s
        )
      );

      let errorMessage = 'Failed to update status';

      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = 'Status not found';
        } else if (err.response.status === 403) {
          errorMessage = 'You do not have permission to update this status';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection';
      }

      toast.error(errorMessage);
    }
  };

  const handleDeleteStatus = async (status: StatusConfig) => {
    // Allow deletion only if user has delete permission
    if (!canDeleteStatus) {
      toast.error('You do not have permission to delete statuses');
      return;
    }
    if (status.isSystem) {
      toast.error('System statuses cannot be deleted');
      return;
    }
    if (status.isDefault) {
      toast.error('Cannot delete default statuses');
      return;
    }

    if (!status.id) {
      toast.error('Cannot delete status: Invalid status ID');
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading('Deleting status...');

      await deleteStatus(status.id);

      // Update local state by refetching from backend
      await fetchStatuses();

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Status "${status.name}" deleted successfully`);

      // Clear status cache so other components refresh
      clearStatusCache();

    } catch (err: any) {
      // Handle different types of errors
      let errorMessage = 'Failed to delete status';

      if (err.response) {
        // Server responded with error
        if (err.response.status === 404) {
          errorMessage = 'Status not found';
        } else if (err.response.status === 403) {
          errorMessage = 'You do not have permission to delete this status';
        } else if (err.response.status === 409) {
          errorMessage = 'Cannot delete status that is currently in use';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection';
      }

      toast.error(errorMessage);
    }
  };

  // Filter statuses by category
  type CategoryTab = 'project' | 'task';
  const getStatusesByCategory = (category: CategoryTab) => {
    return statuses.filter(status => status.category === category);
  };

  // Helper function to render status dropdown items
  const renderStatusDropdownItems = (status: StatusConfig) => {
    // Show dropdown only if user has edit or delete permissions
    if (!canEditStatus && !canDeleteStatus) {
      return null;
    }

    if (status.isSystem) {
      return (
        <DropdownMenuItem disabled>
          <Edit className="h-4 w-4 mr-2" />
          Edit (System)
        </DropdownMenuItem>
      );
    }

    return (
      <>
        {canEditStatus && (
          <DropdownMenuItem onClick={() => handleEditStatus(status)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </DropdownMenuItem>
        )}
        {canEditStatus && (
          <DropdownMenuItem onClick={() => handleToggleStatusActive(status)}>
            <Power className="h-4 w-4 mr-2" />
            {status.isActive ? 'Deactivate' : 'Activate'}
          </DropdownMenuItem>
        )}
        {!status.isDefault && canDeleteStatus && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <DropdownMenuItem className="text-red-600" onSelect={(e) => e.preventDefault()}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Status</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete <strong>"{status.name}"</strong>?
                  <br />
                  <span className="text-red-600 font-medium">This action cannot be undone.</span>
                  {status.description && (
                    <div className="mt-2 text-sm text-gray-600">
                      <strong>Description:</strong> {status.description}
                    </div>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleDeleteStatus(status)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete Status
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </>
    );
  };

  if (isLoadingStatuses) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Project Settings</h1>
          </div>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Statuses...</h2>
            <p className="text-gray-600">Please wait while we fetch your status configurations.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Project Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'statuses' && canCreateStatus && (
            <Button
              onClick={() => {
                setEditingStatus(null);
                setIsStatusDialogOpen(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Status
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="w-full">
          <TabsTrigger value="statuses" className="flex items-center gap-2 w-full">
            <Tag className="h-4 w-4" />
            Status Management
          </TabsTrigger>
        </TabsList>

        <TabsContent value="statuses" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Project Statuses */}
            <Card className="shadow-sm border border-gray-200 rounded-2xl p-0">
              <CardHeader className="pb-2 border-b border-gray-100 bg-gray-50 rounded-t-2xl">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                  <FolderKanban className="h-5 w-5 text-blue-600" />
                  Project Statuses
                </CardTitle>
                <CardDescription className="text-sm text-gray-500">
                  Manage statuses for projects
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 p-4">
                {getStatusesByCategory('project').map((status) => (
                  <div
                    key={status.id}
                    className={`flex items-center justify-between p-3 border rounded-xl transition group ${status.isActive
                      ? 'bg-white hover:bg-blue-50/30'
                      : 'bg-gray-50 hover:bg-gray-100 opacity-60'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0 mr-2 border border-gray-200"
                        style={{ backgroundColor: status.color }}
                      />
                      <div>
                        <p
                          className={`font-medium ${status.isActive ? 'text-gray-900' : 'text-gray-500'
                            }`}
                        >
                          {status.name}
                        </p>
                        {status.description && (
                          <p
                            className={`text-xs ${status.isActive ? 'text-gray-500' : 'text-gray-400'
                              }`}
                          >
                            {status.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={status.isActive}
                        onCheckedChange={() => handleToggleStatusActive(status)}
                        className="data-[state=checked]:bg-blue-600"
                        disabled={status.isSystem || !canEditStatus}
                      />
                      {(canEditStatus || canDeleteStatus) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {renderStatusDropdownItems(status)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Task Statuses */}
            <Card className="shadow-sm border border-gray-200 rounded-2xl p-0">
              <CardHeader className="pb-2 border-b border-gray-100 bg-gray-50 rounded-t-2xl">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                  <ListTodo className="h-5 w-5 text-blue-600" />
                  Task Statuses
                </CardTitle>
                <CardDescription className="text-sm text-gray-500">
                  Manage statuses for tasks
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 p-4">
                {getStatusesByCategory('task').map((status) => (
                  <div
                    key={status.id}
                    className={`flex items-center justify-between p-3 border rounded-xl transition group ${status.isActive
                      ? 'bg-white hover:bg-blue-50/30'
                      : 'bg-gray-50 hover:bg-gray-100 opacity-60'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0 mr-2 border border-gray-200"
                        style={{ backgroundColor: status.color }}
                      />
                      <div>
                        <p
                          className={`font-medium ${status.isActive ? 'text-gray-900' : 'text-gray-500'
                            }`}
                        >
                          {status.name}
                        </p>
                        {status.description && (
                          <p
                            className={`text-xs ${status.isActive ? 'text-gray-500' : 'text-gray-400'
                              }`}
                          >
                            {status.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={status.isActive}
                        onCheckedChange={() => handleToggleStatusActive(status)}
                        className="data-[state=checked]:bg-blue-600"
                        disabled={status.isSystem || !canEditStatus}
                      />
                      {(canEditStatus || canDeleteStatus) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {renderStatusDropdownItems(status)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Status Dialog */}
      <AddStatusDialog
        open={isStatusDialogOpen}
        onOpenChange={(open) => {
          setIsStatusDialogOpen(open);
          if (!open) {
            // Reset editing status when dialog closes
            setTimeout(() => {
              setEditingStatus(null);
            }, 100);
          }
        }}
        onSubmit={editingStatus ? handleUpdateStatus : handleAddStatus}
        initialData={editingStatus || undefined}
        isEdit={!!editingStatus}
        allowedCategories={['project', 'task']}
      />
    </div>
  );
};

export default ProjectSettings;

